import React, { PureComponent } from 'react';
import {
  CheckboxChecked,
  CheckboxIndeterminate,
} from '@wix/wix-ui-icons-common/system';
import { st, classes } from './Checkbox.st.css';
import Text from '../Text';
import { InjectedFocusableProps, withFocusable } from '../common/Focusable';

import { generateID } from '../utils/generateId';
import Tooltip from '../Tooltip';
import * as DATA_ATTR from './DataAttr';
import {
  dataHooks,
  LABEL_TEXT_SIZE,
  SELECTION_AREA,
  V_ALIGN,
  SELECTION_AREA_SKIN,
} from './constants';
import { CheckboxProps } from '.';
import { ZIndex } from '../common/ZIndex';
import semanticClassNames from './Checkbox.semanticClassNames';
import { classes as commonClasses } from '../common/styles/visuallyHidden.st.css';
import deprecationLog from '../utils/deprecationLog';

/** a simple WixStyle checkbox */
export class Checkbox extends PureComponent<
  InjectedFocusableProps<CheckboxProps, HTMLDivElement>
> {
  static displayName = 'Checkbox';
  static defaultProps: CheckboxProps = {
    checked: false,
    size: LABEL_TEXT_SIZE.MEDIUM,
    selectionArea: SELECTION_AREA.NONE,
    vAlign: V_ALIGN.CENTER,
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => e.stopPropagation(),
    hasError: false,
    disabled: false,
    indeterminate: false,
    selectionAreaSkin: SELECTION_AREA_SKIN.FILLED,
  };

  // TODO fix me please. We need to get away from ids.
  _id = `${Checkbox.displayName}-${generateID()}`;

  checkboxRef = React.createRef<HTMLDivElement>();
  inputRef = React.createRef<HTMLInputElement>();

  constructor(props: InjectedFocusableProps<CheckboxProps, HTMLDivElement>) {
    super(props);
    if (typeof props.errorMessage !== 'undefined') {
      deprecationLog(
        '<Checkbox/> - prop "errorMessage" is deprecated and will be removed in next major release, please use "tooltipContent" instead.',
      );
    }
  }

  focus = () => {
    this.inputRef.current && this.inputRef.current.focus();
  };

  _handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    this.props.onChange?.(event);
  };

  _getDataAttributes = () => {
    const { checked, indeterminate, disabled, hasError } = this.props;
    return {
      [DATA_ATTR.DATA_CHECK_TYPE]: indeterminate
        ? DATA_ATTR.CHECK_TYPES.INDETERMINATE
        : checked
          ? DATA_ATTR.CHECK_TYPES.CHECKED
          : DATA_ATTR.CHECK_TYPES.UNCHECKED,
      [DATA_ATTR.DATA_HAS_ERROR]: hasError && !disabled,
      [DATA_ATTR.DATA_DISABLED]: disabled,
    };
  };

  render() {
    const {
      id = this._id,
      checked,
      indeterminate,
      disabled,
      required,
      hasError,
      errorMessage,
      onChange,
      selectionArea,
      vAlign,
      size,
      children,
      dataHook,
      focusableOnFocus,
      focusableOnBlur,
      className,
      tooltipProps,
      tooltipContent,
      selectionAreaSkin,
      selectionAreaPadding,
      ellipsis,
      maxLines,
    } = this.props;

    const isTooltipDisabled =
      (tooltipProps && tooltipProps.disabled) ||
      disabled ||
      (!tooltipContent && (!hasError || !errorMessage));

    return (
      <div
        data-hook={dataHook}
        className={st(
          classes.root,
          {
            vAlign,
            selectionArea,
            selectionAreaSkin,
            disabled,
            size,
            error: hasError && !disabled,
            selection: indeterminate
              ? 'indeterminate'
              : checked
                ? 'checked'
                : 'unchecked',
            indeterminate,
            ellipsis,
          },
          className,
        )}
        {...this._getDataAttributes()}
      >
        <input
          ref={this.inputRef}
          className={commonClasses.visuallyHidden}
          data-hook={dataHooks.input}
          type="checkbox"
          id={id}
          checked={checked}
          disabled={disabled}
          required={required}
          aria-checked={indeterminate ? 'mixed' : checked}
          onChange={onChange}
          onFocus={focusableOnFocus}
          onBlur={focusableOnBlur}
        />
        <label
          htmlFor={id}
          data-hook={dataHooks.label}
          className={classes.label}
          onClick={e => e.stopPropagation()}
        >
          <div
            className={classes.labelInner}
            style={{ padding: selectionAreaPadding }}
          >
            <Tooltip
              dataHook={dataHooks.boxTooltip}
              disabled={isTooltipDisabled}
              content={tooltipContent || errorMessage || ' '}
              textAlign="center"
              maxWidth={230}
              exitDelay={150}
              zIndex={ZIndex.checkbox}
              {...tooltipProps}
            >
              <div className={classes.outer}>
                <div
                  data-hook={dataHooks.box}
                  className={st(
                    classes.checkbox,
                    semanticClassNames.inputContainer,
                  )}
                >
                  <div className={classes.inner}>
                    {indeterminate ? (
                      <CheckboxIndeterminate />
                    ) : (
                      <CheckboxChecked />
                    )}
                  </div>
                </div>
              </div>
            </Tooltip>
            {children && (
              <Text
                size={size}
                weight="thin"
                dataHook={dataHooks.children}
                className={classes.children}
                ellipsis={ellipsis}
                maxLines={maxLines}
              >
                {children}
              </Text>
            )}
          </div>
        </label>
      </div>
    );
  }
}

export default withFocusable<CheckboxProps, HTMLDivElement>(Checkbox);
